/**
 * Quick start template for Vibeship DevTools
 * Single file that handles all API endpoints
 */

export function getQuickStartTemplate(framework: any): string {
  const isAppRouter = framework.isAppRouter;
  
  if (isAppRouter) {
    return getAppRouterQuickStartTemplate();
  } else {
    return getPagesRouterQuickStartTemplate();
  }
}

function getAppRouterQuickStartTemplate(): string {
  return `/**
 * Vibeship DevTools API Route
 * Generated by: npx vibeship init --level quick
 * 
 * This single file handles all Vibeship API endpoints
 */

import { NextRequest, NextResponse } from 'next/server';
import { readFileSync, statSync, existsSync } from 'fs';
import { join, relative } from 'path';
import { glob } from 'glob';

// Types
interface Task {
  id: string;
  type: 'todo' | 'fixme' | 'hack' | 'note' | 'bug' | 'warning';
  text: string;
  file: string;
  line: number;
  priority: 'low' | 'medium' | 'high';
  metadata?: Record<string, any>;
}

interface FileInfo {
  path: string;
  name: string;
  size: number;
  lastModified: string;
}

// Task patterns to search for
const TASK_PATTERNS = {
  todo: /TODO:?\\s*(.+)/gi,
  fixme: /FIXME:?\\s*(.+)/gi,
  hack: /HACK:?\\s*(.+)/gi,
  note: /NOTE:?\\s*(.+)/gi,
  bug: /BUG:?\\s*(.+)/gi,
  warning: /WARNING:?\\s*(.+)/gi,
};

// GET handler for various endpoints
export async function GET(request: NextRequest) {
  const url = new URL(request.url);
  const pathname = url.pathname;
  
  try {
    // Health check
    if (pathname.endsWith('/health')) {
      return NextResponse.json({ 
        status: 'ok', 
        timestamp: new Date().toISOString(),
        version: '1.0.0'
      });
    }
    
    // Tasks endpoint
    if (pathname.endsWith('/tasks')) {
      const tasks = await scanForTasks();
      return NextResponse.json({ 
        tasks,
        stats: {
          total: tasks.length,
          byType: getTaskStats(tasks)
        }
      });
    }
    
    // Tasks stream endpoint (SSE)
    if (pathname.endsWith('/tasks/stream')) {
      const encoder = new TextEncoder();
      const stream = new ReadableStream({
        start(controller) {
          // Send initial tasks
          scanForTasks().then(tasks => {
            const data = JSON.stringify({ type: 'update', tasks });
            controller.enqueue(encoder.encode(\`data: \${data}\\n\\n\`));
          });
          
          // Send heartbeat every 30 seconds
          const interval = setInterval(() => {
            controller.enqueue(encoder.encode(':heartbeat\\n\\n'));
          }, 30000);
          
          // Cleanup on close
          request.signal.addEventListener('abort', () => {
            clearInterval(interval);
            controller.close();
          });
        },
      });
      
      return new Response(stream, {
        headers: {
          'Content-Type': 'text/event-stream',
          'Cache-Control': 'no-cache',
          'Connection': 'keep-alive',
        },
      });
    }
    
    // Files endpoint
    if (pathname.endsWith('/files')) {
      const pattern = url.searchParams.get('pattern') || '**/*.{md,mdx}';
      const files = await listFiles(pattern);
      return NextResponse.json({ files });
    }
    
    // File content endpoint
    if (pathname.endsWith('/files/content')) {
      const path = url.searchParams.get('path');
      if (!path) {
        return NextResponse.json({ error: 'Path parameter required' }, { status: 400 });
      }
      
      const content = await readFile(path);
      return NextResponse.json({ content, path });
    }
    
    // Default API info
    return NextResponse.json({ 
      message: 'Vibeship DevTools API',
      endpoints: {
        health: '/api/vibeship/health',
        tasks: '/api/vibeship/tasks',
        'tasks/stream': '/api/vibeship/tasks/stream',
        files: '/api/vibeship/files',
        'files/content': '/api/vibeship/files/content',
      },
      docs: 'https://vibeship.dev/docs'
    });
    
  } catch (error) {
    console.error('API Error:', error);
    return NextResponse.json(
      { 
        error: 'Internal server error',
        message: error instanceof Error ? error.message : 'Unknown error'
      },
      { status: 500 }
    );
  }
}

// POST handler for write operations
export async function POST(request: NextRequest) {
  const url = new URL(request.url);
  const pathname = url.pathname;
  
  try {
    // File write endpoint
    if (pathname.endsWith('/files/content')) {
      // Check if writes are disabled
      if (process.env.VIBESHIP_DISABLE_FILE_WRITES === 'true') {
        return NextResponse.json(
          { error: 'File writes are disabled' },
          { status: 403 }
        );
      }
      
      const { path, content } = await request.json();
      
      if (!path || content === undefined) {
        return NextResponse.json(
          { error: 'Path and content required' },
          { status: 400 }
        );
      }
      
      // Simple path validation
      if (path.includes('..') || path.startsWith('/')) {
        return NextResponse.json(
          { error: 'Invalid path' },
          { status: 400 }
        );
      }
      
      // Write file
      const fullPath = join(process.cwd(), path);
      const { writeFileSync, mkdirSync } = await import('fs');
      const { dirname } = await import('path');
      
      mkdirSync(dirname(fullPath), { recursive: true });
      writeFileSync(fullPath, content, 'utf8');
      
      return NextResponse.json({ success: true, path });
    }
    
    // AI chat endpoint (placeholder)
    if (pathname.endsWith('/ai/chat')) {
      const { messages } = await request.json();
      
      return NextResponse.json({
        response: 'AI chat requires API key configuration. Please set up your AI provider in the settings.',
        requiresSetup: true
      });
    }
    
    return NextResponse.json(
      { error: 'Endpoint not found' },
      { status: 404 }
    );
    
  } catch (error) {
    console.error('API Error:', error);
    return NextResponse.json(
      { 
        error: 'Internal server error',
        message: error instanceof Error ? error.message : 'Unknown error'
      },
      { status: 500 }
    );
  }
}

// Task scanning implementation
async function scanForTasks(): Promise<Task[]> {
  const tasks: Task[] = [];
  const cwd = process.cwd();
  
  // Default directories to scan
  const scanPaths = ['src', 'app', 'pages', 'components', 'lib', 'docs', 'README.md'];
  const filePatterns = ['**/*.{ts,tsx,js,jsx,md,mdx}'];
  
  for (const scanPath of scanPaths) {
    const fullPath = join(cwd, scanPath);
    if (!existsSync(fullPath)) continue;
    
    try {
      const stat = statSync(fullPath);
      
      if (stat.isFile()) {
        // Single file
        const content = readFileSync(fullPath, 'utf8');
        extractTasksFromFile(content, scanPath, tasks);
      } else {
        // Directory
        const files = glob.sync(filePatterns, {
          cwd: fullPath,
          ignore: ['**/node_modules/**', '**/.next/**', '**/dist/**'],
          absolute: false,
        });
        
        for (const file of files) {
          const filePath = join(fullPath, file);
          const content = readFileSync(filePath, 'utf8');
          const relativePath = relative(cwd, filePath);
          extractTasksFromFile(content, relativePath, tasks);
        }
      }
    } catch (error) {
      console.error(\`Error scanning \${scanPath}:\`, error);
    }
  }
  
  return tasks;
}

function extractTasksFromFile(content: string, filePath: string, tasks: Task[]): void {
  const lines = content.split('\\n');
  
  lines.forEach((line, index) => {
    for (const [type, pattern] of Object.entries(TASK_PATTERNS)) {
      const matches = [...line.matchAll(pattern)];
      
      for (const match of matches) {
        tasks.push({
          id: \`\${filePath}-\${index}-\${type}-\${tasks.length}\`,
          type: type as Task['type'],
          text: match[1].trim(),
          file: filePath,
          line: index + 1,
          priority: inferPriority(match[1]),
        });
      }
    }
  });
}

function inferPriority(text: string): Task['priority'] {
  const lowercased = text.toLowerCase();
  if (lowercased.includes('urgent') || lowercased.includes('asap') || lowercased.includes('critical')) {
    return 'high';
  }
  if (lowercased.includes('low') || lowercased.includes('minor') || lowercased.includes('nice to have')) {
    return 'low';
  }
  return 'medium';
}

function getTaskStats(tasks: Task[]): Record<string, number> {
  const stats: Record<string, number> = {};
  
  for (const task of tasks) {
    stats[task.type] = (stats[task.type] || 0) + 1;
  }
  
  return stats;
}

// File operations
async function listFiles(pattern: string): Promise<FileInfo[]> {
  const cwd = process.cwd();
  
  try {
    const files = glob.sync(pattern, {
      cwd,
      ignore: ['**/node_modules/**', '**/.next/**', '**/dist/**', '**/.git/**'],
      absolute: false,
    });
    
    return files.map(file => {
      const fullPath = join(cwd, file);
      const stat = statSync(fullPath);
      
      return {
        path: file,
        name: file.split('/').pop() || file,
        size: stat.size,
        lastModified: stat.mtime.toISOString(),
      };
    });
  } catch (error) {
    console.error('Error listing files:', error);
    return [];
  }
}

async function readFile(path: string): Promise<string> {
  // Simple path validation
  if (path.includes('..') || path.startsWith('/')) {
    throw new Error('Invalid path');
  }
  
  const fullPath = join(process.cwd(), path);
  
  if (!existsSync(fullPath)) {
    throw new Error('File not found');
  }
  
  return readFileSync(fullPath, 'utf8');
}
`;
}

function getPagesRouterQuickStartTemplate(): string {
  return `/**
 * Vibeship DevTools API Route (Pages Router)
 * Generated by: npx vibeship init --level quick
 * 
 * This single file handles all Vibeship API endpoints
 */

import type { NextApiRequest, NextApiResponse } from 'next';
import { readFileSync, statSync, existsSync } from 'fs';
import { join, relative } from 'path';

// Types
interface Task {
  id: string;
  type: 'todo' | 'fixme' | 'hack' | 'note' | 'bug' | 'warning';
  text: string;
  file: string;
  line: number;
  priority: 'low' | 'medium' | 'high';
}

// Task patterns
const TASK_PATTERNS = {
  todo: /TODO:?\\s*(.+)/gi,
  fixme: /FIXME:?\\s*(.+)/gi,
  hack: /HACK:?\\s*(.+)/gi,
  note: /NOTE:?\\s*(.+)/gi,
  bug: /BUG:?\\s*(.+)/gi,
  warning: /WARNING:?\\s*(.+)/gi,
};

export default async function handler(
  req: NextApiRequest,
  res: NextApiResponse
) {
  // Enable CORS
  res.setHeader('Access-Control-Allow-Origin', '*');
  res.setHeader('Access-Control-Allow-Methods', 'GET, POST, OPTIONS');
  res.setHeader('Access-Control-Allow-Headers', 'Content-Type');
  
  if (req.method === 'OPTIONS') {
    return res.status(200).end();
  }
  
  const { action } = req.query;
  
  try {
    // Handle different actions based on query parameter
    switch (action) {
      case 'health':
        return res.json({ 
          status: 'ok', 
          timestamp: new Date().toISOString(),
          version: '1.0.0'
        });
        
      case 'tasks':
        const tasks = await scanForTasks();
        return res.json({ 
          tasks,
          stats: {
            total: tasks.length,
            byType: getTaskStats(tasks)
          }
        });
        
      case 'files':
        const pattern = req.query.pattern as string || '**/*.{md,mdx}';
        const files = await listFiles(pattern);
        return res.json({ files });
        
      case 'file-content':
        if (req.method === 'GET') {
          const path = req.query.path as string;
          if (!path) {
            return res.status(400).json({ error: 'Path parameter required' });
          }
          
          const content = await readFile(path);
          return res.json({ content, path });
        }
        
        if (req.method === 'POST') {
          // Check if writes are disabled
          if (process.env.VIBESHIP_DISABLE_FILE_WRITES === 'true') {
            return res.status(403).json({ error: 'File writes are disabled' });
          }
          
          const { path, content } = req.body;
          
          if (!path || content === undefined) {
            return res.status(400).json({ error: 'Path and content required' });
          }
          
          // Write file
          await writeFile(path, content);
          return res.json({ success: true, path });
        }
        break;
        
      default:
        return res.json({ 
          message: 'Vibeship DevTools API',
          endpoints: {
            health: '/api/vibeship?action=health',
            tasks: '/api/vibeship?action=tasks',
            files: '/api/vibeship?action=files',
            'file-content': '/api/vibeship?action=file-content',
          },
          docs: 'https://vibeship.dev/docs'
        });
    }
    
  } catch (error) {
    console.error('API Error:', error);
    return res.status(500).json({ 
      error: 'Internal server error',
      message: error instanceof Error ? error.message : 'Unknown error'
    });
  }
}

// Implementation functions (similar to App Router version)
async function scanForTasks(): Promise<Task[]> {
  const tasks: Task[] = [];
  const cwd = process.cwd();
  
  // Default directories to scan
  const scanPaths = ['src', 'pages', 'components', 'lib', 'docs', 'README.md'];
  
  for (const scanPath of scanPaths) {
    const fullPath = join(cwd, scanPath);
    if (!existsSync(fullPath)) continue;
    
    try {
      await scanDirectory(fullPath, scanPath, tasks);
    } catch (error) {
      console.error(\`Error scanning \${scanPath}:\`, error);
    }
  }
  
  return tasks;
}

async function scanDirectory(dirPath: string, basePath: string, tasks: Task[]): Promise<void> {
  const entries = readdirSync(dirPath);
  
  for (const entry of entries) {
    const fullPath = join(dirPath, entry);
    const stat = statSync(fullPath);
    
    if (stat.isDirectory() && !entry.startsWith('.') && entry !== 'node_modules') {
      await scanDirectory(fullPath, join(basePath, entry), tasks);
    } else if (stat.isFile() && /\\.(ts|tsx|js|jsx|md|mdx)$/.test(entry)) {
      const content = readFileSync(fullPath, 'utf8');
      extractTasksFromFile(content, join(basePath, entry), tasks);
    }
  }
}

function extractTasksFromFile(content: string, filePath: string, tasks: Task[]): void {
  const lines = content.split('\\n');
  
  lines.forEach((line, index) => {
    for (const [type, pattern] of Object.entries(TASK_PATTERNS)) {
      const matches = [...line.matchAll(pattern)];
      
      for (const match of matches) {
        tasks.push({
          id: \`\${filePath}-\${index}-\${type}-\${tasks.length}\`,
          type: type as Task['type'],
          text: match[1].trim(),
          file: filePath,
          line: index + 1,
          priority: 'medium',
        });
      }
    }
  });
}

function getTaskStats(tasks: Task[]): Record<string, number> {
  const stats: Record<string, number> = {};
  
  for (const task of tasks) {
    stats[task.type] = (stats[task.type] || 0) + 1;
  }
  
  return stats;
}

async function listFiles(pattern: string): Promise<any[]> {
  // Simplified file listing
  const cwd = process.cwd();
  const files: any[] = [];
  
  const scanDir = (dir: string, base: string = '') => {
    const entries = readdirSync(dir);
    
    for (const entry of entries) {
      const fullPath = join(dir, entry);
      const relativePath = join(base, entry);
      
      if (statSync(fullPath).isFile() && entry.endsWith('.md')) {
        files.push({
          path: relativePath,
          name: entry,
          size: statSync(fullPath).size,
        });
      }
    }
  };
  
  try {
    scanDir(cwd);
  } catch (error) {
    console.error('Error listing files:', error);
  }
  
  return files;
}

async function readFile(path: string): Promise<string> {
  if (path.includes('..') || path.startsWith('/')) {
    throw new Error('Invalid path');
  }
  
  const fullPath = join(process.cwd(), path);
  
  if (!existsSync(fullPath)) {
    throw new Error('File not found');
  }
  
  return readFileSync(fullPath, 'utf8');
}

async function writeFile(path: string, content: string): Promise<void> {
  if (path.includes('..') || path.startsWith('/')) {
    throw new Error('Invalid path');
  }
  
  const fullPath = join(process.cwd(), path);
  const { writeFileSync, mkdirSync } = await import('fs');
  const { dirname } = await import('path');
  
  mkdirSync(dirname(fullPath), { recursive: true });
  writeFileSync(fullPath, content, 'utf8');
}
`;
}